import * as React from 'react';
import { Category } from '@/pages/DiscoverCategories/types/CategoryTypes';
import { CategoryNamesAndIds } from './constants/constant';
import { getCategoryLoaded } from '@/redux/modules/category/category.selectors';
import { H1 } from '@liveauctioneers/hammer-ui-core/text';
import { useAppSelector } from '@/redux/hooks';
import DiscoverCategoriesMeta from '@/pages/DiscoverCategories/meta/DiscoverCategoriesMeta';
import DropDown from './components/DiscoverCategoriesDropDown/DropDown';
import FormattedMessage from '@/components/Formatted/FormattedMessageText';
import OtherCategories from './components/DiscoverCategories/components/OtherCategories/OtherCategories';
import OtherCategoriesDropDown from '@/pages/DiscoverCategories/components/DiscoverCategories/components/OtherCategories/OtherCategoriesDropDown/OtherCategoriesDropDown';
import SideBar from './components/Sidebar/Sidebar';
import styled, { useTheme } from 'styled-components';
import SubCategory from './components/DiscoverCategoriesSubcategory/DiscoverCategoriesSubcategory';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

const DiscoverCategories = () => {
    const {
        breakpoints: { mobileWidthNum },
    } = useTheme();
    const categoryRefs = React.useRef<HTMLDivElement[]>([]);
    const scrollToCategory = React.useCallback((index: number) => {
        categoryRefs.current[index]?.scrollIntoView({ behavior: 'smooth' });
    }, []);
    const isMobile = useIsNarrowerThan(mobileWidthNum + 1);
    const orderedCategoryIds = Object.values(CategoryNamesAndIds);
    const categoryNames = Object.keys(CategoryNamesAndIds);

    // TODO: Currently, we're loading the array of categories onto the loaded value.
    //  This is different from all our other slices, where loaded is the time the category was loaded, and byId is the actual value.
    //  To avoid rewriting the entire repo around the category slice, I'm leaving this as type any[], and this should be fixed in a later op story.
    const categories = useAppSelector(getCategoryLoaded) as {
        [id: number]: Category[];
    };

    return (
        <DiscoveryPageContainer>
            <DiscoverCategoriesMeta />
            <StyledPageContentContainer>
                <SideBar
                    isMobile={isMobile}
                    scrollToCategory={scrollToCategory}
                />
                <StyledCategoryContentContainer>
                    {isMobile && (
                        <MobileExploreCategoriesTitle>
                            <FormattedMessage id="discoverPage.exploreCategories" />
                        </MobileExploreCategoriesTitle>
                    )}
                    {orderedCategoryIds.map((id, index) => {
                        if (id <= 0) {
                            return null;
                        }
                        return isMobile ? (
                            <DropDown
                                categories={Array.isArray(categories[id]) ? categories[id] : []}
                                index={index}
                                key={id}
                                title={categoryNames[index]}
                            />
                        ) : (
                            <SubCategory
                                category={Array.isArray(categories[id]) ? categories[id] : []}
                                key={id}
                                ref={(element) => {
                                    categoryRefs.current[index] = element;
                                }}
                                title={categoryNames[index]}
                            />
                        );
                    })}
                    {isMobile ? (
                        <OtherCategoriesDropDown />
                    ) : (
                        <OtherCategories
                            ref={(element) => {
                                categoryRefs.current[orderedCategoryIds.length] = element;
                            }}
                        />
                    )}
                </StyledCategoryContentContainer>
            </StyledPageContentContainer>
        </DiscoveryPageContainer>
    );
};

export default DiscoverCategories;

const DiscoveryPageContainer = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    min-height: 400px;
    padding: 0 20px 64px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto;

    @media (max-width: 1200px) {
        margin-left: 0;
        min-width: 55%;
        max-width: 100%;
        min-height: 300px;
    }
`;

const StyledPageContentContainer = styled.div`
    display: flex;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        display: unset;
    }
`;

const StyledCategoryContentContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const MobileExploreCategoriesTitle = styled(H1)`
    margin: 24px 0;
`;
